import React from 'react'
import { ImageSize, ImageBreakpoint } from '../../common/props'
// import NextImage from 'next/image'
import NextImage from 'next/image'
import { useTheme } from '@mui/material'

export type Props = {
  /** Image url used as default image and for responsive images */
  imageUrl: string
  /** Alt text for img element */
  altText: string
  /** Different images at specific breakpoints using picture wrapper */
  images?: ImageBreakpoint[]
  /** Image breakpoint helpers for suggesting to browser which size image to retrieve */
  sizes?: ImageSize[]
  /** Image widths for responsive images in srcSet, only used if using imageUrl and not for images array */
  imageWidths?: number[]
  /** Class name for img element */
  className?: string
  /** Used only for helping browser know how much space to reserve before CSS loads */
  width?: number | `${number}`
  /** Used only for helping browser know how much space to reserve before CSS loads */
  height?: number | `${number}`
  /** Prevent dragging image if set to false */
  draggable?: boolean
  /** Image click for zoom in gallery */
  onClick?: () => void
  layout?: 'fill' | 'responsive'
  loading?: 'lazy' | 'eager'
  priority?: boolean
  dataTestId?: string
}
// We need to provide the images with blured url to tackle CLS
const shimmer = (w: number, h: number) => `
    <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <defs>
      </defs>
      <rect width="${w}" height="${h}" fill="#eee" />
      <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
      <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
    </svg>`

const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str)

const ResponsiveImage = (props: Props) => {
  const {
    imageUrl,
    altText,
    className,
    width,
    height,
    draggable = false,
    onClick,
    priority,
    loading,
    dataTestId,
  } = props
  const theme = useTheme()
  const sizes = props.sizes
    ?.map((size) =>
      size.query && size.breakpoint
        ? `(${size.query}: ${theme.breakpoints.values[size.breakpoint]}px) ${size.width}`
        : `${size.width}`
    )
    .join(', ')
  return (
    <NextImage
      src={imageUrl}
      alt={altText}
      sizes={sizes}
      width={width}
      height={height}
      className={className}
      onClick={onClick}
      draggable={draggable}
      unoptimized
      priority={priority}
      {...(priority && { fetchPriority: 'high' })}
      placeholder={'blur'}
      blurDataURL={`data:image/svg+xml;base64,${toBase64(
        shimmer(parseInt(width?.toString() ?? '100%', 10), parseInt(height?.toString() ?? '100%', 10))
      )}`}
      style={{
        maxWidth: width && '100%',
        height: height && 'inherit',
      }}
      fill={!(width && height)}
      loading={loading}
      data-testId={dataTestId}
    />
  )
}

const ArtDirectedImage = (props: Props) => {
  const {
    imageUrl,
    images,
    altText,
    className,
    width,
    height,
    draggable = false,
    onClick,
    loading,
    priority,
    dataTestId,
  } = props
  const theme = useTheme()
  return (
    <button className={className} onClick={onClick} data-testid={dataTestId}>
      <picture>
        {images?.map((image) => (
          <source
            key={`${image.filename}-${image.query}-${image.breakpoint}`}
            media={
              image.query && image.breakpoint && `(${image.query}: ${theme.breakpoints.values[image.breakpoint]}px)`
            }
            srcSet={image.filename}
            sizes={image.sizes}
          />
        ))}
        <NextImage
          src={imageUrl}
          alt={altText}
          draggable={draggable}
          priority={priority}
          unoptimized
          width={width}
          height={height}
          placeholder={'blur'}
          blurDataURL={`data:image/svg+xml;base64,${toBase64(
            shimmer(parseInt(width?.toString() ?? '100%', 10), parseInt(height?.toString() ?? '100%', 10))
          )}`}
          fill={!(width && height)}
          loading={loading}
        />
      </picture>
    </button>
  )
}

/** Use either imageUrl, imageWidths and sizes for a responsive image with different widths,
 * or images for an art directed image (wrapped in picture), with different urls at different breakpoints. */
const Image = (props: Props) =>
  props.images?.length ? <ArtDirectedImage {...props} /> : <ResponsiveImage {...props} />

export default Image
